// src/components/production/ReportGenerator.jsx

import React, { useState } from 'react';
import { exportToCSV, formatCurrency } from '../../utils/reportingUtils';

const styles = {
    container: {
        background: 'white',
        borderRadius: '12px',
        padding: '25px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '25px'
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    filterSection: {
        display: 'flex',
        gap: '15px',
        marginBottom: '25px',
        flexWrap: 'wrap'
    },
    filterSelect: {
        padding: '10px 15px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        fontSize: '14px'
    },
    exportButton: {
        padding: '10px 20px',
        background: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: '#f8fafc',
        padding: '15px 20px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#374151',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    td: {
        padding: '15px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
    },
    plannedBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    inProgressBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    completedBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    criticalBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    normalBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    }
};

function ReportGenerator({ reportType, reportData, onExport }) {
    const [dateFilter, setDateFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const handleExport = (format) => {
        if (format === 'csv') {
            const csvData = exportToCSV(reportData, reportType);
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${reportType}-report.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            alert('PDF export functionality would be implemented here');
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'Planlandı': { style: styles.plannedBadge, text: 'Planlandı' },
            'Devam Ediyor': { style: styles.inProgressBadge, text: 'Devam Ediyor' },
            'Tamamlandı': { style: styles.completedBadge, text: 'Tamamlandı' },
            'Kritik Seviye': { style: styles.criticalBadge, text: 'Kritik Seviye' },
            'Yeterli Stok': { style: styles.normalBadge, text: 'Yeterli Stok' },
            'Stokta Yok': { style: styles.criticalBadge, text: 'Stokta Yok' }
        };

        const badge = badges[status];
        if (badge) {
            return <span style={{...styles.statusBadge, ...badge.style}}>{badge.text}</span>;
        }
        return <span style={{...styles.statusBadge, backgroundColor: '#e2e8f0', color: '#64748b'}}>{status}</span>;
    };

    const renderTable = () => {
        switch (reportType) {
            case 'production-status':
                return (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün</th>
                                <th style={styles.th}>Reçete</th>
                                <th style={styles.th}>Miktar</th>
                                <th style={styles.th}>Tamamlanan</th>
                                <th style={styles.th}>İlerleme</th>
                                <th style={styles.th}>Durum</th>
                                <th style={styles.th}>Öncelik</th>
                                <th style={styles.th}>Atölye</th>
                                <th style={styles.th}>Bitiş Tarihi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.orders.map(order => (
                                <tr key={order.id} style={styles.trHover}>
                                    <td style={{...styles.td, fontWeight: '600'}}>{order.product}</td>
                                    <td style={styles.td}>{order.recipe}</td>
                                    <td style={styles.td}>{order.quantity} adet</td>
                                    <td style={styles.td}>{order.completed} adet</td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ 
                                                width: '80px', 
                                                height: '6px', 
                                                background: '#e2e8f0', 
                                                borderRadius: '3px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{ 
                                                    width: `${order.progress}%`, 
                                                    height: '100%', 
                                                    background: '#10b981',
                                                    borderRadius: '3px'
                                                }} />
                                            </div>
                                            <span>{order.progress}%</span>
                                        </div>
                                    </td>
                                    <td style={styles.td}>{getStatusBadge(order.status)}</td>
                                    <td style={styles.td}>
                                        {order.priority === 'high' && <span style={{color: '#dc2626', fontWeight: '600'}}>Yüksek</span>}
                                        {order.priority === 'normal' && <span style={{color: '#f59e0b', fontWeight: '600'}}>Normal</span>}
                                        {order.priority === 'low' && <span style={{color: '#10b981', fontWeight: '600'}}>Düşük</span>}
                                    </td>
                                    <td style={styles.td}>{order.assignedTo}</td>
                                    <td style={styles.td}>{order.endDate.toLocaleDateString('tr-TR')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );

            case 'raw-material-stock':
                return (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Hammadde</th>
                                <th style={styles.th}>Kod</th>
                                <th style={styles.th}>Stok</th>
                                <th style={styles.th}>Kritik Seviye</th>
                                <th style={styles.th}>Durum</th>
                                <th style={styles.th}>Depo Detayları</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.materials.map(material => (
                                <tr key={material.id} style={styles.trHover}>
                                    <td style={{...styles.td, fontWeight: '600'}}>{material.name}</td>
                                    <td style={styles.td}>{material.code || '-'}</td>
                                    <td style={styles.td}>{material.totalStock} {material.unit}</td>
                                    <td style={styles.td}>{material.criticalLevel} {material.unit}</td>
                                    <td style={styles.td}>{getStatusBadge(material.status)}</td>
                                    <td style={styles.td}>
                                        {material.warehouseDetails.map((detail, index) => (
                                            <div key={index} style={{ fontSize: '12px' }}>
                                                {detail.warehouseName}: {detail.quantity} {detail.unit}
                                            </div>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );

            default:
                return (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                            Rapor Bulunamadı
                        </h3>
                        <p>Seçilen rapor türü için veri bulunamadı.</p>
                    </div>
                );
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    {reportType === 'production-status' && 'Üretim Durum Raporu'}
                    {reportType === 'raw-material-stock' && 'Hammadde Stok Durumu'}
                    {reportType === 'subcontractor-production' && 'Fason Bazlı Üretim Raporu'}
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        onClick={() => handleExport('csv')}
                        style={styles.exportButton}
                    >
                        📊 CSV Olarak İndir
                    </button>
                    <button 
                        onClick={() => handleExport('pdf')}
                        style={{...styles.exportButton, background: '#3b82f6'}}
                    >
                        📄 PDF Olarak İndir
                    </button>
                </div>
            </div>

            <div style={styles.filterSection}>
                <select 
                    style={styles.filterSelect}
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                >
                    <option value="all">Tüm Tarihler</option>
                    <option value="today">Bugün</option>
                    <option value="week">Bu Hafta</option>
                    <option value="month">Bu Ay</option>
                </select>
                
                <select 
                    style={styles.filterSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">Tüm Durumlar</option>
                    {reportType === 'production-status' && (
                        <>
                            <option value="planned">Planlandı</option>
                            <option value="inProgress">Devam Ediyor</option>
                            <option value="completed">Tamamlandı</option>
                        </>
                    )}
                    {reportType === 'raw-material-stock' && (
                        <>
                            <option value="critical">Kritik Seviye</option>
                            <option value="normal">Yeterli Stok</option>
                            <option value="outOfStock">Stokta Yok</option>
                        </>
                    )}
                </select>
            </div>

            <div style={styles.tableContainer}>
                {renderTable()}
            </div>

            {reportData.summary && (
                <div style={{ 
                    marginTop: '25px', 
                    padding: '20px', 
                    background: '#f8fafc', 
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                }}>
                    <h3 style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: '#0f172a', 
                        margin: '0 0 15px 0' 
                    }}>
                        Özet
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        {Object.entries(reportData.summary).map(([key, value]) => (
                            <div key={key} style={{ 
                                background: 'white', 
                                padding: '15px', 
                                borderRadius: '8px', 
                                border: '1px solid #e2e8f0' 
                            }}>
                                <div style={{ 
                                    fontSize: '24px', 
                                    fontWeight: '700', 
                                    color: '#0f172a',
                                    marginBottom: '5px'
                                }}>
                                    {typeof value === 'number' ? value.toLocaleString('tr-TR') : value}
                                </div>
                                <div style={{ 
                                    fontSize: '14px', 
                                    color: '#64748b' 
                                }}>
                                    {key}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReportGenerator;